import React, { useMemo } from 'react';
import { format, isSameDay, isToday, isTomorrow, parseISO, isPast, startOfDay, isSameMonth } from 'date-fns';
import { Calendar as CalendarIcon, AlertCircle, Clock, CheckCircle2 } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

// Define status categories
const INCOMPLETE_STATUSES = [
  'Scheduled',
  'Assigned',
  'Driver En Route',
  'Passenger Picked Up',
  'Arrived at Destination',
  'Payment Pending',
  'pending'
];

const FINAL_STATUSES = ['Completed', 'Cancelled'];

const BookingCalendar = ({ bookings, selectedDate, onSelectDate }) => {
  // Create a Set of booked dates for O(1) lookup
  const bookedDatesSet = useMemo(() => {
    const datesSet = new Set();
    
    console.log('📅 Building booked dates set from', bookings.length, 'bookings');
    
    bookings.forEach(booking => {
      if (!booking.date) {
        console.warn('⚠️  Booking missing date:', booking.id);
        return;
      }
      
      // Normalize to YYYY-MM-DD format
      const dateStr = booking.date; // Already in YYYY-MM-DD format
      datesSet.add(dateStr);
      console.log(`  ✅ Added date: ${dateStr} (${booking.status})`);
    });
    
    console.log(`📊 Total unique booked dates: ${datesSet.size}`);
    console.log('Booked dates:', Array.from(datesSet).sort());
    
    return datesSet;
  }, [bookings]);

  // Calculate booking stats for each date
  const dateBookingMap = useMemo(() => {
    const map = new Map();
    
    bookings.forEach(booking => {
      const dateStr = booking.date;
      if (!dateStr) return;
      
      if (!map.has(dateStr)) {
        map.set(dateStr, {
          total: 0,
          incomplete: 0,
          completed: 0,
          cancelled: 0,
          bookings: []
        });
      }
      
      const stats = map.get(dateStr);
      stats.total++;
      stats.bookings.push(booking);
      
      if (INCOMPLETE_STATUSES.includes(booking.status)) {
        stats.incomplete++;
      } else if (booking.status === 'Completed') {
        stats.completed++;
      } else if (booking.status === 'Cancelled') {
        stats.cancelled++;
      }
    });
    
    return map;
  }, [bookings]);

  // Get stats for selected date
  const selectedStats = useMemo(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    return dateBookingMap.get(dateStr) || { total: 0, incomplete: 0, completed: 0, cancelled: 0, bookings: [] };
  }, [selectedDate, dateBookingMap]);

  // Get today's and tomorrow's incomplete bookings
  const todayIncomplete = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const stats = dateBookingMap.get(today);
    return stats?.incomplete || 0;
  }, [dateBookingMap]);

  const tomorrowIncomplete = useMemo(() => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    const stats = dateBookingMap.get(tomorrow);
    return stats?.incomplete || 0;
  }, [dateBookingMap]);

  // Get overdue bookings
  const overdueBookings = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return bookings.filter(booking => {
      return booking.date < today && INCOMPLETE_STATUSES.includes(booking.status);
    });
  }, [bookings]);

  // Custom day renderer - THIS IS KEY
  const DayContent = (day) => {
    const dateStr = format(day.date, 'yyyy-MM-dd');
    const isBooked = bookedDatesSet.has(dateStr);
    const stats = dateBookingMap.get(dateStr);
    const hasIncomplete = stats && stats.incomplete > 0;
    const isSelected = isSameDay(day.date, selectedDate);
    const isCurrentMonth = isSameMonth(day.date, day.displayMonth);
    
    // Log for debugging
    if (isBooked) {
      console.log(`🎨 Rendering booked date: ${dateStr}, incomplete: ${hasIncomplete}`);
    }
    
    return (
      <div
        className={`
          relative w-full h-full flex items-center justify-center
          ${!isCurrentMonth ? 'opacity-40' : ''}
        `}
      >
        <span className="relative z-10">{format(day.date, 'd')}</span>
        
        {/* Booking indicator dot */}
        {isBooked && (
          <div
            className={`
              absolute bottom-1 left-1/2 transform -translate-x-1/2
              w-1 h-1 rounded-full
              ${hasIncomplete ? 'bg-yellow-400' : 'bg-green-400'}
              shadow-lg
            `}
          />
        )}
      </div>
    );
  };

  // Custom modifiers
  const modifiers = {
    booked: (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return bookedDatesSet.has(dateStr);
    },
    incomplete: (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const stats = dateBookingMap.get(dateStr);
      return stats && stats.incomplete > 0;
    },
    completed: (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const stats = dateBookingMap.get(dateStr);
      return stats && stats.incomplete === 0 && stats.total > 0;
    }
  };

  const modifiersClassNames = {
    booked: 'has-booking-indicator',
    incomplete: 'has-incomplete-booking',
    completed: 'has-complete-booking'
  };

  return (
    <div className="space-y-6">
      {/* Header Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Today's Bookings */}
        <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 p-4 rounded-xl border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-300 text-sm mb-1">Today</p>
              <p className="text-white text-2xl font-bold">{todayIncomplete}</p>
            </div>
            <CalendarIcon className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        {/* Tomorrow's Bookings */}
        <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 p-4 rounded-xl border border-green-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-300 text-sm mb-1">Tomorrow</p>
              <p className="text-white text-2xl font-bold">{tomorrowIncomplete}</p>
            </div>
            <Clock className="w-8 h-8 text-green-400" />
          </div>
        </div>

        {/* Overdue Bookings */}
        {overdueBookings.length > 0 && (
          <div className="bg-gradient-to-br from-red-600/20 to-red-800/20 p-4 rounded-xl border border-red-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-300 text-sm mb-1">Overdue</p>
                <p className="text-white text-2xl font-bold">{overdueBookings.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>
        )}
      </div>

      {/* Overdue Alert */}
      {overdueBookings.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <p className="text-red-400 font-semibold">Overdue Bookings</p>
              <p className="text-red-300 text-sm mt-1">
                {overdueBookings.length} booking{overdueBookings.length !== 1 ? 's' : ''} from past dates still incomplete
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="booking-calendar-wrapper bg-gray-800/50 rounded-xl p-6 border border-yellow-500/20">
        <style>{`
          /* CRITICAL: Strong visual highlighting for booked dates */
          .booking-calendar-wrapper .rdp-day_button.has-incomplete-booking {
            background: linear-gradient(135deg, rgba(234, 179, 8, 0.3), rgba(249, 115, 22, 0.3)) !important;
            border: 2px solid rgba(234, 179, 8, 0.8) !important;
            border-radius: 8px !important;
            font-weight: 700 !important;
            box-shadow: 0 0 16px rgba(234, 179, 8, 0.5) !important;
            animation: pulse-glow 2s ease-in-out infinite;
            position: relative !important;
          }
          
          .booking-calendar-wrapper .rdp-day_button.has-complete-booking {
            background: rgba(34, 197, 94, 0.25) !important;
            border: 2px solid rgba(34, 197, 94, 0.7) !important;
            border-radius: 8px !important;
            color: rgb(34, 197, 94) !important;
            font-weight: 600 !important;
            box-shadow: 0 0 12px rgba(34, 197, 94, 0.4) !important;
          }
          
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 16px rgba(234, 179, 8, 0.5);
              transform: scale(1);
            }
            50% {
              box-shadow: 0 0 24px rgba(234, 179, 8, 0.7);
              transform: scale(1.05);
            }
          }
          
          /* Override any conflicting styles */
          .booking-calendar-wrapper .rdp-day_button {
            position: relative;
          }
          
          /* Selected date should still be visible */
          .booking-calendar-wrapper .rdp-day_button.rdp-day_selected {
            background: rgba(59, 130, 246, 0.3) !important;
            border-color: rgba(59, 130, 246, 0.8) !important;
          }
          
          /* Selected + Booked */
          .booking-calendar-wrapper .rdp-day_button.rdp-day_selected.has-incomplete-booking {
            background: linear-gradient(135deg, rgba(234, 179, 8, 0.3), rgba(59, 130, 246, 0.3)) !important;
            border: 2px solid rgba(234, 179, 8, 0.8) !important;
            box-shadow: 0 0 16px rgba(234, 179, 8, 0.5), 0 0 8px rgba(59, 130, 246, 0.3) !important;
          }
          
          /* Today styling */
          .booking-calendar-wrapper .rdp-day_button.rdp-day_today {
            font-weight: bold;
          }
          
          /* Mobile responsive */
          @media (max-width: 640px) {
            .booking-calendar-wrapper .rdp-day_button.has-incomplete-booking,
            .booking-calendar-wrapper .rdp-day_button.has-complete-booking {
              border-width: 2px !important;
            }
          }
        `}</style>
        
        <DayPicker
          mode="single"
          selected={selectedDate}
          onSelect={onSelectDate}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          className="rdp-custom"
          components={{
            DayContent
          }}
        />
      </div>

      {/* Selected Date Info */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-yellow-500/20">
        <p className="text-gray-400 text-sm mb-2">Selected Date:</p>
        <p className="text-white font-semibold text-lg mb-4">
          {format(selectedDate, 'MMMM d, yyyy')}
        </p>
        
        {selectedStats.total > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Total Bookings:</span>
              <span className="text-white font-semibold">{selectedStats.total}</span>
            </div>
            {selectedStats.incomplete > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-yellow-400 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  Incomplete:
                </span>
                <span className="text-yellow-400 font-semibold">{selectedStats.incomplete}</span>
              </div>
            )}
            {selectedStats.completed > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-400 flex items-center">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Completed:
                </span>
                <span className="text-green-400 font-semibold">{selectedStats.completed}</span>
              </div>
            )}
            {selectedStats.cancelled > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-red-400">Cancelled:</span>
                <span className="text-red-400 font-semibold">{selectedStats.cancelled}</span>
              </div>
            )}
            
            {/* Bookings List */}
            <div className="mt-4 space-y-2">
              {selectedStats.bookings.map((booking, index) => (
                <div key={booking.id} className="text-sm bg-gray-700/30 rounded p-2">
                  <p className="text-white font-semibold">{booking.passengerName}</p>
                  <p className="text-gray-400 text-xs">
                    {booking.time} • {booking.pickupLocation} → {booking.dropoffLocation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm italic">No bookings for this date</p>
        )}
      </div>

      {/* Legend - Dynamic Counts */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-yellow-500/20">
        <p className="text-gray-400 text-sm mb-3 font-semibold">Calendar Legend:</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-yellow-500 bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">●</span>
            </div>
            <span className="text-sm text-gray-300 whitespace-nowrap">
              Pending ({bookings.filter(b => INCOMPLETE_STATUSES.includes(b.status)).length})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-green-500 bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-green-400">●</span>
            </div>
            <span className="text-sm text-gray-300 whitespace-nowrap">
              Completed ({bookings.filter(b => b.status === 'Completed').length})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
